import getApiService from 'modules/api/services/get'
import { prepareOptionsApiUtil } from 'modules/api/utils'
import createReduxAction from 'modules/redux/actions/create'
import REDUX_ACTION from 'modules/redux/constants/action'
import { REDUX_STATUS } from '@gymaholic/packages'
import isLoadingSelector from 'modules/redux/selectors/is-loading'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import isInstanceReduxUtil from 'modules/redux/utils/is-instance'
import REDUX_OPTION from 'modules/redux/constants/option.js'
import { getObjectUtil } from 'packages/src'

/**
 * Sets the given `path` status as loading.
 * @private
 * @param {Object} action
 *        {string} payload.type
 *        {string} payload.path
 */
function loadingReduxAction (action) {
  return {
    type: action.type,
    reduxType: REDUX_ACTION.LOAD,
    payload: action.payload
  }
}

/**
 * Sets the given `path` status as success and set the data.
 * @private
 * @param {Object} action
 *        {Object} action.payload
 *        {string} [action.payload.path]
 *        {string} [action.payload.domainListPath]
 *        {Object} [action.payload.data]
 */
function loadSuccessReduxAction (action) {
  return {
    type: action.type + '_SUCCESS',
    payload: action.payload,
    reduxType: REDUX_ACTION.LOAD_SUCCESS
  }
}

/**
 * Sets the given `path` status as error and set the error.
 * @private
 * @param {Object} action
 *        {Object} action.payload
 *        {string} [action.payload.path]
 *        {string} [action.payload.domainListPath]
 *        {Object} action.payload.error
 */
function loadErrorReduxAction (action) {
  return {
    type: action.type + '_ERROR',
    payload: action.payload,
    reduxType: REDUX_ACTION.LOAD_ERROR
  }
}

function loadReduxAction (action) {
  return async (dispatch, getState) => {
    const {
      payload
    } = action
    let {
      options
    } = action
    const {
      endpoint,
      domainListPath,
      data,
      className
    } = payload
    let {
      path
    } = payload

    // If it's already loading, no-op
    if (
      path &&
      isLoadingSelector(path)(getState())
    ) {
      return
    }

    // If it's already loading, no-op
    if (
      domainListPath &&
      isLoadingSelector(domainListPath)(getState())
    ) {
      return
    }

    // If it's an instance that needs to be created then loaded, create it
    const instance = path
      ? getObjectUtil(getState(), path)
      : null
    if (
      path &&
      !instance &&
      data
    ) {
      const newInstance = dispatch(
        createReduxAction({
          type: action.type + '_CREATE_BEFORE_LOAD',
          payload: {
            data: {
              data
            },
            domainListPath
          }
        })
      )
      path = getPathReduxUtil(newInstance)
    }

    dispatch(
      loadingReduxAction({
        type: action.type,
        payload: {
          path,
          domainListPath
        }
      })
    )
    try {
      options = prepareOptionsApiUtil(options, getState())
      const response = await getApiService(endpoint, options)
      const responseData = response.data && Object.values(response.data)[0]

      if (
        Array.isArray(responseData) &&
        responseData.length
      ) {
        dispatch(
          createReduxAction({
            type: action.type + '_CREATE_AFTER_LOAD',
            payload: {
              data: responseData,
              status: REDUX_STATUS.SUCCESS,
              domainListPath,
              className
            }
          })
        )
      }

      const shouldKeepPath = options[REDUX_OPTION.SHOULD_KEEP_PATH]
      // If it's loading a single instance, update the instance
      const domainOrInstance = getObjectUtil(getState(), path)

      if (
        isInstanceReduxUtil(domainOrInstance) &&
        responseData
      ) {
        const instanceAfterLoad = dispatch(
          createReduxAction({
            type: action.type + '_UPDATE_AFTER_LOAD',
            payload: {
              domainListPath,
              data: {
                data: responseData,
                status: REDUX_STATUS.SUCCESS
              },
              existingInstance: domainOrInstance,
              path: shouldKeepPath ? path : null
            }
          })
        )
        path = shouldKeepPath ? path : getPathReduxUtil(instanceAfterLoad)
      }

      dispatch(
        loadSuccessReduxAction({
          type: action.type,
          payload: {
            path,
            domainListPath: path || shouldKeepPath ? null : domainListPath
          }
        })
      )

      return response
    } catch (error) {
      dispatch(
        loadErrorReduxAction({
          type: action.type,
          payload: {
            path,
            domainListPath,
            error
          }
        })
      )
      throw error
    }
  }
}

export default loadReduxAction
